/* =====================================================================
 * custom.js — Movado Group behavior overrides
 * ---------------------------------------------------------------------
 * Movado's code-level JavaScript lives HERE, never in theme.js.
 * theme.js is a generated esbuild bundle (Prestige) that auto-registers
 * ~95 custom elements on load; editing it by hand is overwritten on every
 * theme upgrade. This file is loaded as an ES module in layout/theme.liquid
 * AFTER theme.js, so the theme's custom elements are already defined when
 * this runs.
 *
 * HOW TO EXTEND (match how the theme extends itself — do NOT monkeypatch):
 *   • Hook the theme's document-level CustomEvent bus, e.g.
 *       document.addEventListener('cart:change',   (e) => { ... e.detail.cart ... });
 *       document.addEventListener('variant:change',(e) => { ... e.detail.variant ... });
 *     Other events: cart:refresh, line-item:change, product:rerender,
 *     facet:update, theme:loading:start / theme:loading:end,
 *     dialog:after-show / dialog:after-hide. (Full list:
 *     https://support.maestrooo.com/article/764-javascript-events-exposed-by-the-theme)
 *   • Add NEW behavior as a NEW custom element:
 *       class MovadoThing extends HTMLElement { connectedCallback() { ... } }
 *       if (!customElements.get('movado-thing'))
 *         customElements.define('movado-thing', MovadoThing);
 *     Then emit <movado-thing> from a snippet/section.
 *   • theme.js DOES export a public ESM API (importmap entry "theme"). Reuse it
 *     instead of reinventing — subclass a component or call a utility:
 *       import { Drawer, fetchCart, createMediaImg } from 'theme';
 *       class MovadoDrawer extends Drawer { ... }      // reuses focus-trap, anim, a11y
 *       customElements.define('movado-drawer', MovadoDrawer);
 *     Exported: component classes (Drawer, Modal, Popover, DialogElement,
 *     ProductForm, CartDrawer, VariantPicker, …) and utilities (fetchCart,
 *     cachedFetch, createMediaImg, imageLoaded, generateSrcset, throttle,
 *     debounce, deepQuerySelector, extractSectionId, matchesMediaQuery,
 *     mediaQueryListener, announceStatus, announceError, videoLoaded, waitForEvent).
 *   • Vendored libs are also on the importmap:
 *       import { animate, inView, timeline, scroll } from 'vendor';  // Motion v10.18.0
 *
 * This is a module: it is deferred by default and runs after HTML parse.
 * ===================================================================== */

// Movado overrides go below.

import { throttle, cachedFetch, matchesMediaQuery, announceStatus, generateSrcset } from 'theme';
import { initDataLayer } from 'data-layer';

initDataLayer();

// Global "Back to top" button (sections/back-to-top.liquid). `inert` (not a
// class) drives visibility, so it's out of the tab order/AT tree while still
// letting custom.css animate opacity/transform.
class MovadoBackToTop extends HTMLElement {
  #onScroll = throttle(() => this.#updateVisibility());

  connectedCallback() {
    this.threshold = Number(this.dataset.threshold) || 300;
    this.button = this.querySelector('.back-to-top__button');

    this.button.addEventListener('click', this.#handleClick);
    window.addEventListener('scroll', this.#onScroll, { passive: true });

    this.#updateVisibility();
  }

  disconnectedCallback() {
    this.button.removeEventListener('click', this.#handleClick);
    window.removeEventListener('scroll', this.#onScroll);
    this.#onScroll.cancel();
  }

  #updateVisibility() {
    this.toggleAttribute('inert', window.scrollY <= this.threshold);
  }

  #handleClick = (event) => {
    event.preventDefault();
    this.setAttribute('inert', '');
    this.button.blur();

    window.scrollTo({
      top: 0,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  };
}

if (!customElements.get('movado-back-to-top')) {
  customElements.define('movado-back-to-top', MovadoBackToTop);
}

// PLP swatch preview. Card swatches emit media under data-movado-* (option-value.liquid),
// which disables theme.js's ProductCard swap — this is the card image's only writer.
const FADE_DURATION = 150;

// animate() doesn't honor prefers-reduced-motion the way CSS transitions do;
// collapse the fade to an instant swap when it's set.
function fadeDuration() {
  return matchesMediaQuery('motion-reduce') ? 0 : FADE_DURATION;
}

// Server-rendered images (may be custom.card_image), restored on the default swatch.
const defaultCardImages = new WeakMap();

function snapshotImage(imageElement) {
  return { src: imageElement.src, srcset: imageElement.srcset, alt: imageElement.alt };
}

function applyImage(imageElement, image) {
  imageElement.src = image.src;
  imageElement.srcset = image.srcset;
  imageElement.alt = image.alt;
}

// Widths come from the live img's own srcset so they track the theme's ladder.
function srcsetWidths(imageElement) {
  const srcset = imageElement.getAttribute('srcset') || '';
  const widths = Array.from(srcset.matchAll(/(\d+)w/g), (match) => Number(match[1]));
  return widths.length ? widths : [Number(imageElement.getAttribute('width')) || 800];
}

// Media without a usable preview image (video, 3D model) resolves to null.
function resolveMediaImage(input, attribute, imageElement) {
  const media = JSON.parse(input.getAttribute(attribute) || 'null');
  const preview = media && media.preview_image;

  if (!preview || !preview.src) return null;

  // Media JSON src is protocol-relative; normalize for the swap's no-op guard.
  const src = preview.src.startsWith('//') ? `https:${preview.src}` : preview.src;

  return { src, srcset: generateSrcset(preview, srcsetWidths(imageElement)), alt: media.alt || '' };
}

function resolveFetchedImage(html) {
  const galleryImg = html.querySelector('product-gallery[form^="product-form-main-"] .product-gallery__media.is-initial img');

  if (galleryImg) return { src: galleryImg.src, srcset: galleryImg.srcset, alt: galleryImg.alt };

  // Model-led or media-less products render no gallery <img>.
  const ogImage = html.querySelector('meta[property="og:image:secure_url"], meta[property="og:image"]');
  return ogImage && { src: ogImage.content, srcset: '', alt: '' };
}

function resolveSwatchTarget(card, input) {
  if (input.defaultChecked) return defaultCardImages.get(card);

  const primary = card.querySelector('.product-card__image--primary');

  if (!primary) return null;

  return {
    primary: resolveMediaImage(input, 'data-movado-variant-media', primary),
    secondary: resolveMediaImage(input, 'data-movado-variant-secondary-media', card.querySelector('.product-card__image--secondary') || primary),
  };
}

async function swapCardImage(card, input, target) {
  const primary = card.querySelector('.product-card__image--primary');

  if (!primary || !target || !target.primary || primary.src === target.primary.src) return;

  const secondary = card.querySelector('.product-card__image--secondary');

  if (!defaultCardImages.has(card)) {
    defaultCardImages.set(card, {
      primary: snapshotImage(primary),
      secondary: secondary ? snapshotImage(secondary) : null,
    });
  }

  await fadeSwap([primary], () => {
    // Radios are per-card groups: unchecked means a newer swatch superseded this one.
    if (!input.checked) return;

    applyImage(primary, target.primary);

    if (secondary && target.secondary) applyImage(secondary, target.secondary);
  });
}

document.addEventListener('pointerover', prefetchSwatch, true);
document.addEventListener('focusin', prefetchSwatch);

function prefetchSwatch(event) {
  // Warms every colorway in the group, not just the hovered swatch, for more lead time.
  const fieldset = event.target.closest('.product-card__info fieldset');

  if (fieldset && !fieldset.dataset.swatchesPrefetched) {
    fieldset.dataset.swatchesPrefetched = 'true';
    fieldset.querySelectorAll('[data-product-url]').forEach((swatchInput) => {
      cachedFetch(swatchInput.getAttribute('data-product-url')).catch(() => {});
    });
  }

  // pointerover hits the label, focusin the sr-only input — resolve both to
  // the input carrying the swatch data, to warm its image too.
  const swatchLabel = event.target.closest('.product-card__info .color-swatch');
  const input = swatchLabel ? swatchLabel.previousElementSibling : event.target.closest('.product-card__info [type="radio"]');

  if (!input) return;

  // Warm the same srcset candidate the click-time swap will pick.
  const primary = input.closest('product-card')?.querySelector('.product-card__image--primary');
  const image = primary && !input.defaultChecked && resolveMediaImage(input, 'data-movado-variant-media', primary);

  if (image) {
    const img = new Image();
    img.sizes = primary.sizes;
    img.srcset = image.srcset;
    img.src = image.src;
  }
}

document.addEventListener('change', async (event) => {
  const input = event.target;

  if (!input.matches('.product-card__info [type="radio"]')) return;

  const card = input.closest('product-card');

  if (!card) return;

  if (input.hasAttribute('data-movado-variant-media') || input.defaultChecked) {
    swapCardImage(card, input, resolveSwatchTarget(card, input)).catch(() => {});
  }

  if (input.hasAttribute('data-variant-price-html')) {
    const priceListElement = card.querySelector('.price-list');
    const sourcePriceList = new DOMParser()
      .parseFromString(input.getAttribute('data-variant-price-html'), 'text/html')
      .querySelector('price-list');

    if (priceListElement && sourcePriceList) {
      await fadeSwap([priceListElement], () => {
        if (input.checked) updateCardPriceList(priceListElement, sourcePriceList);
      });
    }

    if (!input.checked) return;

    // The swatch label's is-disabled class reflects this variant's availability.
    const isVariantAvailable = !(input.nextElementSibling?.classList.contains('is-disabled') ?? false);
    toggleSoldOutBadge(card, !isVariantAvailable);

    if (input.hasAttribute('data-variant-badge-html')) {
      const badgeDocument = new DOMParser().parseFromString(input.getAttribute('data-variant-badge-html'), 'text/html');
      updateSaleBadge(card, badgeDocument.querySelector('on-sale-badge'));
    }

    announceSwatchChange(card);
    updateQuickAdd(card, { variantId: input.getAttribute('data-variant-id'), available: isVariantAvailable });

    return;
  }

  // Combined-listing colorways are separate products; all_products caps at 20 handles/page.
  const productUrl = input.getAttribute('data-product-url');

  if (!productUrl) return;

  removeCustomBadges(card);

  try {
    const response = await cachedFetch(productUrl);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');

    if (!input.checked) return;

    if (!input.hasAttribute('data-movado-variant-media')) {
      swapCardImage(card, input, { primary: resolveFetchedImage(html), secondary: null }).catch(() => {});
    }

    // Scope past the fetched page's cart drawer (before <main>) — its cross-sell cards reuse these classes.
    const mainProductSection = html.querySelector('form[id^="product-form-main-"]')?.closest('.shopify-section') || html;
    const newTitle = mainProductSection.querySelector('.product-title');
    const newPriceList = mainProductSection.querySelector('.price-list');
    const titleElement = card.querySelector('.product-title');
    const priceListElement = card.querySelector('.price-list');

    await fadeSwap([titleElement, priceListElement], () => {
      if (!input.checked) return;

      if (titleElement && newTitle) {
        titleElement.textContent = newTitle.textContent.trim();
      }

      if (priceListElement && newPriceList) {
        // Take only the money text — the fetched price-list uses PDP-size
        // classes, so copying its markup would resize the card's price.
        updateCardPriceList(priceListElement, newPriceList);
      }
    });

    if (!input.checked) return;

    // The connected product's ATC is disabled when it has no available variant.
    const isConnectedProductSoldOut = mainProductSection.querySelector('buy-buttons button')?.hasAttribute('disabled') ?? false;
    // Its resolved variant, read from the main product form. The input is disabled
    // when the PDP has a variant picker, but its value attribute still holds the id.
    const connectedVariantId = html.querySelector('form[id^="product-form-main-"] input[name="id"]')?.value;
    await toggleSoldOutBadge(card, isConnectedProductSoldOut);
    addCustomBadges(card, html);
    updateSaleBadge(card, mainProductSection.querySelector('on-sale-badge')?.cloneNode(true) || null);
    announceSwatchChange(card);
    updateQuickAdd(card, {
      connectedProductHandle: productUrl.split('/products/').pop(),
      connectedVariantId,
      available: !isConnectedProductSoldOut,
    });
  } catch (error) {
    // Fail open: title/price just stay.
  }
});

// Fades elements out together, runs mutate, fades them back in — so separate
// nodes (title, price) change as one coordinated beat.
async function fadeSwap(elements, mutate) {
  const targets = elements.filter(Boolean);
  const duration = fadeDuration();
  const fadeOutAnimations = targets.map((element) =>
    element.animate({ opacity: [1, 0] }, { duration, easing: 'ease-in', fill: 'forwards' })
  );

  await Promise.all(fadeOutAnimations.map((animation) => animation.finished));

  mutate();

  // Cancel the forwards-filled fade-out first, else it wins the composite once
  // the fade-in ends and snaps the element back to opacity 0.
  fadeOutAnimations.forEach((animation) => animation.cancel());
  targets.forEach((element) => element.animate({ opacity: [0, 1] }, { duration, easing: 'ease-in' }));
}

// Swaps only the money text between two price-lists, keeping the card's markup.
function updateCardPriceList(cardPriceList, fetchedPriceList) {
  const cardSalePrice = cardPriceList.querySelector('sale-price');
  const fetchedSalePrice = fetchedPriceList.querySelector('sale-price');
  const cardCompareAtPrice = cardPriceList.querySelector('compare-at-price');
  const fetchedCompareAtPrice = fetchedPriceList.querySelector('compare-at-price');
  const isOnSale = Boolean(fetchedCompareAtPrice);

  if (cardSalePrice && fetchedSalePrice) {
    setMoneyText(cardSalePrice, fetchedSalePrice);
    cardSalePrice.classList.toggle('text-on-sale', isOnSale);
    cardSalePrice.classList.toggle('text-subdued', !isOnSale);
  }

  if (isOnSale && cardSalePrice) {
    if (cardCompareAtPrice) {
      setMoneyText(cardCompareAtPrice, fetchedCompareAtPrice);
    } else {
      const clone = fetchedCompareAtPrice.cloneNode(true);
      clone.className = cardSalePrice.classList.contains('h6') ? 'h6 text-subdued line-through' : 'text-subdued line-through';
      cardSalePrice.after(clone);
    }
  } else if (cardCompareAtPrice) {
    cardCompareAtPrice.remove();
  }
}

function setMoneyText(targetElement, sourceElement) {
  const sourceText = getMoneyTextNode(sourceElement);

  if (!sourceText) return;

  const targetText = getMoneyTextNode(targetElement);

  if (targetText) {
    targetText.textContent = sourceText.textContent;
  } else {
    targetElement.append(sourceText.textContent);
  }
}

function getMoneyTextNode(element) {
  return Array.from(element.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
}

// The swap is silent to screen readers; announce it via the live region.
function announceSwatchChange(card) {
  const title = card.querySelector('.product-title')?.textContent.trim();
  const price = card.querySelector('.price-list sale-price')?.textContent.trim();
  const isSoldOut = Boolean(card.querySelector('sold-out-badge'));
  const message = [title, price, isSoldOut ? window.themeVariables.strings.soldOutBadge : null].filter(Boolean).join(', ');

  if (message) {
    announceStatus(message);
  }
}

// Re-points the card's quick add at the selected swatch (theme.js never does).
// Two shapes (product-card.liquid): a direct-submit form for single-variant
// products, or a <quick-buy-modal> trigger otherwise. A sold-out or un-addable
// colorway hides the button rather than leave a dead one.
function updateQuickAdd(card, { variantId, connectedProductHandle, connectedVariantId, available = true } = {}) {
  const quickBuyModal = card.querySelector('quick-buy-modal');
  const quickAddButton = card.querySelector('.product-card__quick-add-button');

  if (connectedProductHandle) {
    if (quickBuyModal) {
      // The bare handle resolves to the connected product's first variant.
      quickBuyModal.setAttribute('handle', connectedProductHandle);
      setQuickAddVisible(quickAddButton, available);
    } else if (quickAddButton) {
      // Re-point the form at the connected product's resolved variant for a
      // one-click add. Show only when we have that id and it's in stock;
      // otherwise hide (fail closed) rather than add the wrong variant.
      const hiddenVariantInput = card.querySelector('product-form input[name="id"]');
      const canAdd = Boolean(hiddenVariantInput && connectedVariantId && available);

      if (canAdd) {
        hiddenVariantInput.value = connectedVariantId;
      }

      setQuickAddVisible(quickAddButton, canAdd);
    }

    return;
  }

  if (!variantId) return;

  const hiddenVariantInput = card.querySelector('product-form input[name="id"]');

  if (hiddenVariantInput) {
    hiddenVariantInput.value = variantId;
  }

  if (quickBuyModal) {
    quickBuyModal.setAttribute('handle', `${card.getAttribute('handle')}?variant=${variantId}`);
  }

  // Hide the "+" for a sold-out colorway rather than open a modal to it.
  setQuickAddVisible(quickAddButton, available);
}

// hidden → display:none (no competing display rule on this absolute button),
// and drops it from the a11y tree.
function setQuickAddVisible(button, visible) {
  if (!button) return;

  button.hidden = !visible;
}

// Same reasoning as sold-out below: theme.js never revisits the save badge on
// swatch change. newBadge is a variant-accurate <on-sale-badge> or null.
async function updateSaleBadge(card, newBadge) {
  const figure = card.querySelector('.product-card__figure');
  const badgeList = figure?.querySelector('badge-list');
  const currentBadge = card.querySelector('on-sale-badge');

  if (newBadge) {
    if (currentBadge) {
      currentBadge.replaceWith(newBadge);
      return;
    }

    let targetBadgeList = badgeList;

    if (!targetBadgeList && figure) {
      targetBadgeList = Object.assign(document.createElement('badge-list'), { className: 'badge-list badge-list--vertical' });
      figure.prepend(targetBadgeList);
    }

    if (!targetBadgeList) return;

    targetBadgeList.append(newBadge);
    newBadge.animate({ opacity: [0, 1] }, { duration: fadeDuration(), easing: 'ease-in' });
  } else if (currentBadge) {
    await currentBadge.animate({ opacity: [1, 0] }, { duration: fadeDuration(), easing: 'ease-in', fill: 'forwards' }).finished;
    currentBadge.remove();

    if (badgeList && !badgeList.children.length) {
      badgeList.remove();
    }
  }
}

// Shows/hides the card's <sold-out-badge> to match the selected swatch, which
// theme.js doesn't revisit on swatch change.
async function toggleSoldOutBadge(card, isSoldOut) {
  if (!window.themeVariables.settings.showSoldOutBadge) return;

  const figure = card.querySelector('.product-card__figure');
  const badgeList = figure?.querySelector('badge-list');
  const soldOutBadge = card.querySelector('sold-out-badge');

  if (isSoldOut && !soldOutBadge) {
    let targetBadgeList = badgeList;

    if (!targetBadgeList && figure) {
      targetBadgeList = Object.assign(document.createElement('badge-list'), { className: 'badge-list badge-list--vertical' });
      figure.prepend(targetBadgeList);
    }

    if (!targetBadgeList) return;

    const newBadge = Object.assign(document.createElement('sold-out-badge'), {
      className: 'badge badge--sold-out',
      textContent: window.themeVariables.strings.soldOutBadge,
    });
    targetBadgeList.prepend(newBadge);
    newBadge.animate({ opacity: [0, 1] }, { duration: fadeDuration(), easing: 'ease-in' });
  } else if (!isSoldOut && soldOutBadge) {
    await soldOutBadge.animate({ opacity: [1, 0] }, { duration: fadeDuration(), easing: 'ease-in', fill: 'forwards' }).finished;
    soldOutBadge.remove();

    if (badgeList && !badgeList.children.length) {
      badgeList.remove();
    }
  }
}

// Called immediately on swatch change, before the connected-product fetch, so the stale badge doesn't linger.
function removeCustomBadges(card) {
  const badgeList = card.querySelector('.product-card__figure badge-list');

  badgeList?.querySelectorAll('.badge--custom').forEach((badge) => badge.remove());

  if (badgeList && !badgeList.children.length) {
    badgeList.remove();
  }
}

function addCustomBadges(card, sourceDocument) {
  const figure = card.querySelector('.product-card__figure');
  let badgeList = figure?.querySelector('badge-list');
  const newCustomBadges = Array.from(sourceDocument.querySelectorAll('.badge--custom'));

  if (!newCustomBadges.length) return;

  if (!badgeList && figure) {
    badgeList = Object.assign(document.createElement('badge-list'), { className: 'badge-list badge-list--vertical' });
    figure.prepend(badgeList);
  }

  newCustomBadges.reverse().forEach((badge) => badgeList?.prepend(badge.cloneNode(true)));
}

/* ---------------------------------------------------------------------
 * Shopify Forms app embed. The form renders in an open shadow root, out of
 * reach of theme CSS — adopt a constructed stylesheet into each root.
 * Selectors rely on stable app ids/attributes, not hashed class names.
 * ------------------------------------------------------------------- */
const FORMS_EMBED_STYLES = `
  form[data-testid='form'] {
    display: grid !important;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 0.75rem;
  }

  form[data-testid='form'] > * {
    grid-column: 1 / -1;
  }

  /* The first two text fields pair up from the theme's sm breakpoint; full
     width on mobile. Counts only containers with a direct input, so the
     app's lead-capture wrapper div doesn't take a slot. */
  @media screen and (min-width: 700px) {
    form[data-testid='form'] > :nth-child(-n + 2 of div:has(> input)) {
      grid-column: auto;
    }
  }

  /* Theme tokens inherit through the shadow boundary. Line-heights are baked
     into the shorthands (font: resets them); 1.65 matches theme.css's body.
     --movado-forms-button-text is bridged in custom.css — the app's :host
     shadows the theme's name with an incompatible format. */
  :host {
    --forms-base-font-size: var(--text-base, 14px);
    --forms-heading-font-size: var(--text-h2, 22px);
    --forms-body-font: var(--text-font-style, normal) var(--text-font-weight, 400) var(--forms-base-font-size) / 1.65 var(--text-font-family, sans-serif);
    --forms-heading-font: var(--heading-font-style, normal) var(--heading-font-weight, 400) var(--forms-heading-font-size) / var(--heading-line-height, 1.2) var(--heading-font-family, serif);
    --form-field-border-radius: 0px;
    --button-border-radius: 0px;
  }

  /* The app's heading class sets line-height as a longhand — outrank it. */
  :host h2 {
    font-family: var(--heading-font-family);
    font-size: var(--text-h2, 22px);
    font-weight: var(--heading-font-weight);
    font-style: var(--heading-font-style);
    letter-spacing: var(--heading-letter-spacing);
    text-transform: var(--heading-text-transform);
    line-height: var(--heading-line-height, 1.2) !important;
  }

  p, label, input, textarea {
    font-family: var(--text-font-family);
    font-size: var(--text-base, 14px);
    letter-spacing: var(--text-letter-spacing);
  }

  /* Undo the leaked 1.65 body line-height; substring match survives the
     CSS-module hash churning between app builds. */
  [class*='_arrowDownIcon'] {
    line-height: 15px;
  }

  /* Mirror the theme's .button. Submit only: the select toggle's arrow and
     panel anchoring depend on its own font metrics. */
  button[type='submit'] {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    box-sizing: border-box;
    width: 100%;
    font: var(--button-font);
    text-transform: var(--button-text-transform);
    letter-spacing: var(--button-letter-spacing);
    padding: 0.65rem 1.75rem;
    color: rgb(var(--movado-forms-button-text, 255 255 255));
    background: rgb(var(--button-background, 32 32 32));
    border: 1px solid rgb(var(--button-background, 32 32 32));
    border-radius: 0;
    transition: none;
    filter: none;
  }

  /* The app's :hover drops the border and applies an opacity() filter —
     redeclare the full border and neutralize the rest. */
  button[type='submit']:hover {
    background: rgb(var(--button-hover-background, var(--button-background)));
    color: rgb(var(--button-hover-text-color, var(--movado-forms-button-text)));
    border: 1px solid rgb(var(--button-hover-background, var(--button-background)));
    filter: none;
    transform: none;
  }
`;

// Constructed sheet, not an injected <style> — element sheets are inert in
// this root, and adopted sheets win cascade ties by coming last.
const FORMS_EMBED_SHEET = new CSSStyleSheet();
FORMS_EMBED_SHEET.replaceSync(FORMS_EMBED_STYLES);

const observedFormsRoots = new WeakSet();

function styleFormsEmbed(embed) {
  const root = embed.shadowRoot;

  if (!root) return;

  if (!root.adoptedStyleSheets.includes(FORMS_EMBED_SHEET)) {
    root.adoptedStyleSheets = [...root.adoptedStyleSheets, FORMS_EMBED_SHEET];
  }

  // Re-adopt if the app re-renders the root; includes() keeps this idempotent.
  if (!observedFormsRoots.has(root)) {
    observedFormsRoots.add(root);
    new MutationObserver(() => styleFormsEmbed(embed)).observe(root, { childList: true });
  }
}

function styleAllFormsEmbeds() {
  document.querySelectorAll('shopify-forms-embed').forEach(styleFormsEmbed);
}

styleAllFormsEmbeds();
// Upgrades of server-rendered embeds emit no mutations — hook the definition;
// the observer covers embeds inserted later (editor reloads, app popups).
customElements.whenDefined('shopify-forms-embed').then(styleAllFormsEmbeds);
new MutationObserver(styleAllFormsEmbeds).observe(document.documentElement, { childList: true, subtree: true });

/* ---------------------------------------------------------------------
 * Cart discount field. The vendor applies codes only on change (blur), and
 * Enter submits the cart form — straight to checkout. Enter now commits the
 * code, and an apply button covers re-submission of an unchanged value.
 * ------------------------------------------------------------------- */
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' || !event.target.matches('cart-discount-field input:not([type="hidden"])')) return;

  event.preventDefault();
  event.target.blur(); // commits through the vendor's change listener
});

document.addEventListener('click', (event) => {
  const button = event.target.closest('.cart-discount-field__apply');

  if (!button) return;

  const field = button.closest('cart-discount-field');
  const input = field.querySelector('input:not([type="hidden"])');

  // aria-busy guards the double fire when the click itself blurred the input
  // and the change listener is already applying.
  if (field.getAttribute('aria-busy') === 'true' || !input.value.trim()) return;

  input.dispatchEvent(new Event('change', { bubbles: true }));
});

// Form-associated radios (option-value.liquid) submit the cart form on Enter; block it.
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' || !event.target.matches('.variant-picker input[type="radio"]')) return;

  event.preventDefault();
});

// CartDrawer's AJAX refresh never reinvokes this, so Shop Pay/GPay vanish after an update.
document.addEventListener('cart-drawer:refreshed', () => {
  Shopify?.PaymentButton?.init();
});

/* Cart drawer a11y: the AJAX refresh destroys the focused control, and a removal
 * is never announced (theme.js already announces stepper values). Captured on
 * the success-only line-item:change and consumed by the very next refresh, so a
 * failed request leaves no state. Drawer only — theme.js reloads the cart page. */
document.addEventListener('line-item:change', (event) => {
  const lineItem = event.target;
  const drawer = lineItem.closest('cart-drawer');

  if (!drawer) return;

  const removed = event.detail.quantity === 0;
  const lineKey = lineItem.querySelector('[data-line-key]')?.dataset.lineKey;
  const title = lineItem.dataset.title;
  const control = document.activeElement;
  // Mouse/touch never focus the stepper links (Safari especially); only restore for keyboard/AT users.
  const hadFocus = lineItem.contains(control);
  const selector = lineItem.querySelector('quantity-selector');
  const stepperSlot = selector ? Array.from(selector.children).findIndex((child) => child.contains(control)) : -1;
  const wasRemoveLink = control?.matches?.('line-item-quantity > a') ?? false;

  document.addEventListener(
    'cart-drawer:refreshed',
    () => {
      if (!drawer.hasAttribute('open')) return;

      if (hadFocus) {
        const line = lineKey ? drawer.querySelector(`[data-line-key="${lineKey}"]`)?.closest('line-item') : null;
        let target = null;

        if (line && wasRemoveLink) {
          target = line.querySelector('line-item-quantity > a');
        } else if (line && stepperSlot >= 0) {
          const slot = line.querySelector('quantity-selector')?.children[stepperSlot];
          target = slot?.matches('input, a, button') ? slot : slot?.querySelector('input');
        }

        target ??= drawer.querySelector('line-item [data-line-key]') || drawer.shadowRoot?.querySelector('[part~="close-button"]');
        target?.focus({ preventScroll: true });
      }

      if (removed && title) {
        announceStatus(window.themeVariables.strings.cartItemRemovedWithTitle.replace('{{ title }}', () => title));
      }
    },
    { once: true }
  );
});

// Native <shopify-account> sometimes leaks Tab focus out to the browser chrome; recover it.
let lastTabWasInAccount = false;

document.addEventListener('keydown', (event) => {
  lastTabWasInAccount = event.key === 'Tab' && document.activeElement.matches('shopify-account');
}, true);

window.addEventListener('blur', () => {
  if (!lastTabWasInAccount) return;

  lastTabWasInAccount = false;
  document.querySelector('shopify-account')?.closest('li')?.nextElementSibling?.querySelector('a, button')?.focus();
});

// Shopify's video_tag filter has no alt param for its <video> fallback <img>; patch it here.
function altifyVideoFallbackImages(root) {
  const images = root instanceof Element && root.matches('video img') ? [root] : root.querySelectorAll('video img:not([alt])');

  images.forEach((img) => img.setAttribute('alt', ''));
}

altifyVideoFallbackImages(document);
new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node.nodeType === Node.ELEMENT_NODE) altifyVideoFallbackImages(node);
    }
  }
}).observe(document.body, { childList: true, subtree: true });

/* Cordial contact identification. Login is a full-page redirect, so "on
 * login" = any authenticated page load; no-ops until the Cordial app's
 * base script defines crdl. */
const cordialCustomer = window.themeVariables.customer;

if (cordialCustomer?.authenticated && cordialCustomer.email && typeof window.crdl === 'function') {
  window.crdl('contact', { channels: { email: { address: cordialCustomer.email } } });
}

/* Action links: these hashes, entered in any customizer link field (menus,
 * buttons), run a method on click instead of navigating. */
const HASH_ACTIONS = {
  '#gladlychat': () => window.Gladly?.show(),
  '#cookie-preferences': () => window.OneTrust?.ToggleInfoDisplay(),
  '#account': () => {
    // shopify-account's shadow root is closed; clicking its slotted (light DOM)
    // avatar travels the composed path through the internal trigger button.
    const account = document.querySelector('shopify-account');
    (account?.querySelector('[slot]') || account)?.click();
  },
};

// Action links present as buttons to AT — they act, not navigate.
function upgradeActionLinks(root) {
  const links = root instanceof Element && root.matches('a') ? [root] : root.querySelectorAll('a[href*="#"]');

  for (const link of links) {
    if (!HASH_ACTIONS[link.hash]) continue;

    link.setAttribute('role', 'button');
    link.setAttribute('aria-haspopup', 'dialog');
  }
}

upgradeActionLinks(document);

// Re-upgrade links added by section re-renders (facets, editor, rerender API).
new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node.nodeType === Node.ELEMENT_NODE) upgradeActionLinks(node);
    }
  }
}).observe(document.body, { childList: true, subtree: true });

document.addEventListener('click', (event) => {
  // Let modified clicks (new tab, etc.) keep native behavior.
  if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const link = event.target.closest('a[href*="#"]');
  const action = link && HASH_ACTIONS[link.hash];

  if (!action) return;

  event.preventDefault();
  action();
});

// role="button" promises Space activation, which anchors don't do natively.
document.addEventListener('keydown', (event) => {
  if (event.key !== ' ') return;

  const link = event.target.closest?.('a[href*="#"]');
  const action = link && HASH_ACTIONS[link.hash];

  if (!action) return;

  event.preventDefault();
  action();
});

/* Wrapped gift block hydrates async and its loading placeholder renders
 * unchecked. The line item already knows it's gifted (_Gifting property →
 * data-gifting), so mirror the checked attrs while the app loads. */
function primeWrappedGiftBlocks(root) {
  const blocks = root.matches('wrapped-gift-block') ? [root] : root.querySelectorAll('wrapped-gift-block');

  for (const block of blocks) {
    if (block.dataset.movadoGiftPrimed || !block.closest('line-item[data-gifting]')) continue;

    block.dataset.movadoGiftPrimed = 'true';
    block.setAttribute('gifting', '');
    block.setAttribute('gift-option-included', '');

    customElements.whenDefined('wrapped-gift-block').then(() => {
      const shadow = block.shadowRoot;

      if (!shadow) return;

      // The checkmark CSS keys on the button's with-gift-options, not the wrapper's gifting.
      const prime = () => {
        shadow.querySelectorAll('gift-block-launcher:not([gifting])').forEach((launcher) => launcher.setAttribute('gifting', ''));
        shadow.querySelectorAll('.gift-block__launcher:not([with-gift-options])').forEach((button) => button.setAttribute('with-gift-options', ''));
      };

      prime();
      new MutationObserver(prime).observe(shadow, { childList: true, subtree: true });
    });
  }
}

primeWrappedGiftBlocks(document.body);

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node.nodeType === Node.ELEMENT_NODE) primeWrappedGiftBlocks(node);
    }
  }
}).observe(document.body, { childList: true, subtree: true });

/* Wrapped re-syncs the gift box quantity after any cart change and, without a
 * processorCallback, reloads the page to show it. Re-rendering the drawer
 * shows the same thing in place. Prestige isn't one of the cart apps Wrapped
 * recognizes, so this hook is the only non-reload path it offers. */
function installWrappedProcessorCallback() {
  const settings = window.Wrapped?.settings;

  if (!settings || settings.processorCallback) return;

  settings.processorCallback = () => {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  };
}

// The embed is delay-loaded (content-for-header-optimization), so it usually lands after this runs.
installWrappedProcessorCallback();
document.addEventListener('app-embed:loaded', installWrappedProcessorCallback);

/* OCI reservation id: set on the first cart mutation so it predates checkout
 * and persists to the order for all payment methods. Underscore hides it from
 * customer-facing surfaces; the key must match what the OCI backend reads. */
let ociReservationInFlight = false;

document.addEventListener('cart:change', (event) => {
  const attributes = event.detail.cart?.attributes;

  if (ociReservationInFlight || !attributes || attributes._oci_reservation_id) return;

  ociReservationInFlight = true;

  fetch(`${Shopify.routes.root}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes: { _oci_reservation_id: crypto.randomUUID() } }),
  })
    .catch(() => {})
    .finally(() => (ociReservationInFlight = false));
});
